import { FC, useState } from 'react';
import { H2 } from '../../../../common/Headings/Headings.styles';
import Button from '../../../../components/Button/Button';
import { Variant } from '../../../../components/Button/Button.types';
import Table from '../../../../components/Table/Table';
import { showToast } from '../../../../components/Toast/Toast';
import { ChangeSpecResponse } from '../../../../services/menu/menu.types';
import { genericErrorShowToastOptions } from '../../../../utils/useGenericErrorMessage/useGenericErrorMessage';
import { promisePool } from '../../../../utils/promisePool';
import BatchProgressModal from './BatchProgressModal';
import { Buttons, StyledReviewChanges, TableRowWrapper } from './ReviewChanges.styles';
import { ReviewChangesProps } from './ReviewChanges.types';
import { ChangeSpecsStep } from "../ChangeSpecs.types";

// Max menus per PUT /menus request. Large selections are split into sequential
// batches so each request finishes well inside the ~2min API gateway timeout.
export const CHUNK_SIZE = 500;

const ReviewChanges: FC<ReviewChangesProps> = ({
                                                   selectedSpecs,
                                                   selectedMenus,
                                                   mutateAsync,
                                                   setResults,
                                                   setStep
                                               }) => {
    const [isBatching, setIsBatching] = useState(false);
    const [completedChunks, setCompletedChunks] = useState(0);
    const [totalChunks, setTotalChunks] = useState(0);
    const [menusSubmitted, setMenusSubmitted] = useState(0);

    const editSpec = () => setStep(ChangeSpecsStep.SelectSpec);
    const editMenu = () => setStep(ChangeSpecsStep.SelectMenus);

    const changeSpecOperations = selectedSpecs.map((combinedSpec) => {
        return {
            currentSpec: combinedSpec.currentSpecNo,
            desiredSpec: combinedSpec.desiredSpecNo
        }
    })

    const menus = selectedMenus.map((menu) => {
        return menu.id;
    });

    // Split the menu list into chunks of at most CHUNK_SIZE.
    const chunkMenus = (menuList: number[]): number[][] => {
        return menuList.reduce((resultArray: number[][], item, index) => {
            const chunkIndex = Math.floor(index / CHUNK_SIZE);
            if (!resultArray[chunkIndex]) {
                resultArray[chunkIndex] = [];
            }
            resultArray[chunkIndex].push(item);
            return resultArray;
        }, []);
    };

    // Build placeholder failure rows so a failed batch still surfaces in the Confirmation table.
    const buildFailureRows = (chunk: number[]): ChangeSpecResponse[] => {
        return chunk.map((menuNo) => ({
            menuNo,
            newSpecNo: 0,
            description: 'Request failed - menu was not updated.',
            success: false,
            createdDate: new Date().toISOString(),
        }));
    };

    const handleSaveClick = async () => {
        const chunks = chunkMenus(menus);
        if (chunks.length === 0) return;

        const aggregatedResults: ChangeSpecResponse[] = [];
        let hadFailure = false;

        // Each task sends one batch as its own PUT /menus request. Failures are caught
        // so remaining batches still run (continue-and-aggregate).
        const tasks = chunks.map((chunk) => async () => {
            try {
                const response = await mutateAsync({ menus: chunk, operations: changeSpecOperations });
                aggregatedResults.push(...(response.data ?? []));
            } catch (error) {
                hadFailure = true;
                aggregatedResults.push(...buildFailureRows(chunk));
            }
        });

        setIsBatching(true);
        setTotalChunks(chunks.length);
        setCompletedChunks(0);
        setMenusSubmitted(0);

        // concurrency = 1 keeps the requests sequential to avoid overloading the DB / connection pool.
        await promisePool(tasks, 1, (completed) => {
            setCompletedChunks(completed);
            const submitted = chunks.slice(0, completed).reduce((sum, chunk) => sum + chunk.length, 0);
            setMenusSubmitted(submitted);
        });

        setIsBatching(false);
        setResults(aggregatedResults);
        setStep(ChangeSpecsStep.Confirmation);

        if (hadFailure) {
            showToast({
                ...genericErrorShowToastOptions,
                id: 'changeMenuSpecsPartialError',
                label: 'Some menu specs could not be updated. Review the results below.',
            });
        } else {
            showToast({ label: 'Menu(s) updated successfully.', id: 'changeMenuSpecsSuccess' });
        }
    };

    return (
        <StyledReviewChanges>
            <H2>Review your changes</H2>
            <TableRowWrapper>
                <Table
                    rowData={selectedSpecs}
                    showHeader={true}
                    showFooter={false}
                    headerTitle="Selected Specs"
                    headerButtons={[{ label: 'Edit', variant: Variant.Text, onClick: editSpec }]}
                    floatingFiltersHeight={0}
                    columnDefs={[
                        { minWidth: 110, flex: 1, field: 'currentSpecNo', headerName: 'Current Spec' },
                        { minWidth: 110, flex: 1, field: 'currentDescription', headerName: 'Current Description' },
                        { minWidth: 110, flex: 1, field: 'desiredSpecNo', headerName: 'Desired Spec' },
                        { minWidth: 110, flex: 1, field: 'desiredDescription', headerName: 'Desired Description' }
                    ]}
                />
            </TableRowWrapper>
            <TableRowWrapper>
                <Table
                    rowData={selectedMenus}
                    showHeader={true}
                    showFooter={false}
                    headerTitle="Affected Menus"
                    headerButtons={[{ label: 'Edit', variant: Variant.Text, onClick: editMenu }]}
                    floatingFiltersHeight={0}
                    columnDefs={[
                        { minWidth: 220, flex: 1, field: 'parents.store', headerName: 'MxRef' },
                        { minWidth: 320, flex: 2, field: 'id', headerName: 'Menu', resizable: false }
                    ]}
                />
            </TableRowWrapper>
            <Buttons>
                <Button label="Back" variant={Variant.Secondary} onClick={editMenu} disabled={isBatching}/>
                <Button label="Save" testId="SaveChangesButton" onClick={handleSaveClick} isLoading={isBatching}/>
            </Buttons>
            <BatchProgressModal
                open={isBatching}
                completed={completedChunks}
                total={totalChunks}
                menusSubmitted={menusSubmitted}
                menusTotal={menus.length}
            />
        </StyledReviewChanges>
    );
};

export default ReviewChanges;
